import React, { useRef } from 'react'
import { createPortal } from 'react-dom';
import Back_Btn from "../../../assets/images/back-arrow-left.svg";
import Back_Btndark from "../../../assets/images/greenbackarrow.svg";
import { useNavigate } from 'react-router-dom';
import useLanguageTranslate from '../../../hooks/useLanguageTranslate';

/**
 * Back arrow + page label for the sign-in / sign-up screens.
 *
 * On a phone the arrow is pinned in the header row, top-left, where the
 * policy journeys put theirs (40px, 16px in, mirrored for Arabic). It used to
 * sit in the page flow under a fixed header that was otherwise empty on these
 * screens, which pushed every form 72px further down than it needed to be.
 * Both arrows are real buttons now, and the mobile one no longer shows on
 * desktop (it was toggled with `visible`, which never hid it).
 *
 * The pinned arrow is portalled to <body>. These pages render inside Framer
 * Motion wrappers that fade and slide them in, and any opacity or transform
 * below them makes a stacking context: the arrow's z-30 would then only count
 * inside the page, under the fixed header (z-20), until the entrance ended.
 * As part of the header row it belongs at the top level anyway.
 *
 * Going back plays an exit before navigating: the page slides 24px toward the
 * reading end and fades, 160ms on a strong ease-out (an exit, so quicker than
 * the entrance it undoes), then the home page's own fade takes over. Web
 * Animations rather than state, so nothing re-renders mid-exit; reduced
 * motion gets a plain fade. A timer backs up `onfinish`, which a background
 * tab may never deliver, and a second tap during the exit is ignored.
 */
const EXIT_MS = 160
const EXIT_EASE = "cubic-bezier(0.23, 1, 0.32, 1)"
const PAGE_SELECTOR = ".sign_in_page, .verify_otp_page"

const BackBtnWithLabel = ({label}) => {
  const navigate = useNavigate()
  const translate = useLanguageTranslate()
  const labelRef = useRef(null)
  const leavingRef = useRef(false)
  const goBack = () => {
    if (leavingRef.current) return
    const page = labelRef.current?.closest(PAGE_SELECTOR)
    if (!page || typeof page.animate !== "function") {
      navigate("/")
      return
    }
    leavingRef.current = true
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    const towardEnd = getComputedStyle(page).direction === "rtl" ? -24 : 24
    const frames = reduceMotion
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [
          { opacity: 1, transform: "translateX(0)" },
          { opacity: 0, transform: `translateX(${towardEnd}px)` },
        ]
    let done = false
    const leave = () => {
      if (done) return
      done = true
      navigate("/")
    }
    const exit = page.animate(frames, {
      duration: reduceMotion ? 120 : EXIT_MS,
      easing: EXIT_EASE,
      fill: "forwards",
    })
    exit.onfinish = leave
    setTimeout(leave, EXIT_MS + 120)
  }
  return (
    <>
      <button
        type="button"
        aria-label={translate("Back", "رجوع")}
        className='max767:hidden'
        onClick={goBack}
      >
        <img src={Back_Btn} alt="" className='rtl:rotate-180' />
      </button>
      {createPortal(
        <button
          type="button"
          aria-label={translate("Back", "رجوع")}
          className='hidden max767:flex fixed top-[19px] left-4 rtl:left-auto rtl:right-4 z-30 w-10 h-10 items-center justify-center rounded-full transition-transform duration-150 ease-out active:scale-[0.94]'
          onClick={goBack}
        >
          <img src={Back_Btndark} alt="" className='w-10 h-10 rtl:rotate-180' />
        </button>,
        document.body
      )}
      <h5 ref={labelRef} className="max767:mb-2 text-black text-xl not-italic font-medium leading-[120%] ">{label}</h5>
    </>
  );
}

export default BackBtnWithLabel
